import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AlertCircle, Flag, Info } from 'lucide-react';
import DashboardCard from './DashboardCard';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../context/AuthContext';
import { SEVERITY_COLORS } from '../constants';

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #F0F2F5;

  &:last-child { border-bottom: none; }
`;

const IconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color }) => `${$color}1A`};
  color: ${({ $color }) => $color};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Desc = styled.div`
  font-size: 12px;
  color: #6B7280;
  margin-top: 2px;
`;

const ActionBtn = styled(Button)`
  padding: 5px 12px;
  font-size: 12px;
  border-width: 1.5px;
  flex-shrink: 0;
`;

const ICONS = { critical: AlertCircle, warning: Flag, info: Info };

export default function RiskSignalsCard({ slice, setFilters }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { canRead } = useAuth();
  const signals = slice.data?.signals || [];

  const fmtDate = (d) => new Date(d).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const buildTexts = useCallback((signal) => {
    switch (signal.type) {
      case 'criticalCapacity':
        return {
          title: t('leaveDashboard.risks.criticalCapacity.title', { dept: signal.departmentName || '—' }),
          desc: t('leaveDashboard.risks.criticalCapacity.description', {
            pct: signal.percent,
            tomorrow: signal.tomorrowStarting || 0,
          }),
          action: t('leaveDashboard.risks.criticalCapacity.action'),
        };
      case 'burnout':
        return {
          title: t('leaveDashboard.risks.burnout.title', { name: signal.userName, days: signal.days }),
          desc: t('leaveDashboard.risks.burnout.description', {
            dept: signal.departmentName || '—',
            score: signal.score,
          }),
          action: t('leaveDashboard.risks.burnout.action'),
        };
      case 'overlapWeek':
        return {
          title: t('leaveDashboard.risks.overlapWeek.title', { date: signal.date ? fmtDate(signal.date) : '' }),
          desc: t('leaveDashboard.risks.overlapWeek.description', {
            dept: signal.departmentName || '—',
            count: signal.count,
          }),
          action: t('leaveDashboard.risks.overlapWeek.action'),
        };
      case 'sickPattern':
        return {
          title: t('leaveDashboard.risks.sickPattern.title', { name: signal.userName, count: signal.count }),
          desc: t('leaveDashboard.risks.sickPattern.description', { dept: signal.departmentName || '—' }),
          action: t('leaveDashboard.risks.sickPattern.action'),
        };
      case 'expiringBalance':
      default:
        return {
          title: t('leaveDashboard.risks.expiringBalance.title', { count: signal.count }),
          desc: t('leaveDashboard.risks.expiringBalance.description'),
          action: t('leaveDashboard.risks.expiringBalance.action'),
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);

  const handleAction = useCallback((signal) => {
    switch (signal.type) {
      case 'criticalCapacity':
        if (signal.departmentId && setFilters) {
          setFilters((prev) => ({ ...prev, departmentId: signal.departmentId }));
        }
        scrollTo('lvd-capacity');
        break;
      case 'burnout':
        if (signal.userId && canRead('EM')) history.push(`/employees/${signal.userId}`);
        else history.push('/all-leaves');
        break;
      case 'overlapWeek':
        scrollTo('lvd-density');
        break;
      case 'sickPattern':
        if (signal.latestLeaveId) history.push(`/leaves/${signal.latestLeaveId}`);
        break;
      case 'expiringBalance':
      default:
        scrollTo('lvd-balance');
        break;
    }
  }, [canRead, history, setFilters]);

  return (
    <DashboardCard
      cardKey="risk-sinyalleri"
      title={t('leaveDashboard.risks.title')}
      meta={t('leaveDashboard.risks.meta')}
      span={4}
      spanMd={6}
      loading={slice.loading}
      empty={signals.length === 0}
      emptyText={t('leaveDashboard.risks.empty')}
    >
      <div>
        {signals.map((signal, index) => {
          const color = SEVERITY_COLORS[signal.severity] || SEVERITY_COLORS.info;
          const IconComp = ICONS[signal.severity] || Info;
          const texts = buildTexts(signal);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <Row key={`${signal.type}-${index}`}>
              <IconWrap $color={color}><IconComp size={15} /></IconWrap>
              <Body>
                <Title>{texts.title}</Title>
                <Desc>{texts.desc}</Desc>
              </Body>
              <ActionBtn variant="outline" onClick={() => handleAction(signal)}>
                {texts.action}
              </ActionBtn>
            </Row>
          );
        })}
      </div>
    </DashboardCard>
  );
}
